/**
 * The screenshots a run produced, under its results.
 *
 * A log says a test passed; a screenshot shows what the person would have seen.
 * For a failure it is usually faster than reading the trace, and for a pass it is
 * the only cheap way to notice that a page rendered correctly but wrongly — an
 * empty state where content was expected, a broken layout, the wrong theme.
 *
 * Images are fetched through the console, not from public storage, because they
 * show real screens; and they are deleted when their run is pruned, so this list
 * can legitimately come back empty for an old run.
 */

import { useEffect, useState } from 'react';
import { api } from '../api';

interface Props {
  runId: string;
  /** Poll while the run is still going, so the strip fills in as it finishes. */
  finished: boolean;
}

export function Filmstrip({ runId, finished }: Props) {
  const [shots, setShots] = useState<Array<{ index: number; label: string }>>([]);
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.screenshots(runId)
        .then((r) => { if (!cancelled) setShots(r.screenshots); })
        .catch(() => { /* not available yet */ });
    };
    load();
    // They are collected once the container exits, so one more read after the run
    // finishes is what actually populates this.
    const timer = finished ? undefined : setInterval(load, 4000);
    if (finished) setTimeout(load, 1500);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [runId, finished]);

  // An empty strip must say WHY. Returning nothing left the operator wondering
  // whether the feature was broken, when the run had simply captured failures only.
  if (!shots.length) {
    if (!finished) return null;
    return (
      <div className="surface p-4 text-xs" data-testid="filmstrip-empty"
           style={{ color: 'hsl(var(--text-faint))' }}>
        <span className="font-medium">No screenshots for this run.</span>{' '}
        A full regression captures them only for failed tests, because one image per
        test across the platform runs to hundreds of megabytes per run. Tick
        “Screenshot every test” before starting a run to capture all of them, or use
        Smoke, which always does.
      </div>
    );
  }

  return (
    <div className="surface p-4" data-testid="filmstrip">
      <div className="mb-3 flex items-baseline gap-2">
        <h3 className="text-sm font-medium">Screenshots</h3>
        <span className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
          {shots.length} from this run — click to enlarge. Kept for as long as the run is.
        </span>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2">
        {shots.map((shot) => (
          <button
            key={shot.index}
            onClick={() => setOpen(shot.index)}
            data-testid={`filmstrip-${shot.index}`}
            className="surface-raised shrink-0 overflow-hidden p-0 text-left transition hover:opacity-80"
            style={{ width: '13rem' }}
          >
            <img
              src={api.screenshotUrl(runId, shot.index)}
              alt={shot.label}
              loading="lazy"
              className="h-28 w-full bg-black object-cover"
            />
            <span className="block truncate px-2 py-1.5 text-xs" style={{ color: 'hsl(var(--text-muted))' }}>
              {shot.label}
            </span>
          </button>
        ))}
      </div>

      {open !== null && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center p-6"
          style={{ background: 'hsl(var(--bg) / 0.92)' }}
          onClick={() => setOpen(null)}
          data-testid="filmstrip-lightbox"
        >
          <img
            src={api.screenshotUrl(runId, open)}
            alt={shots[open]?.label}
            className="max-h-[85vh] max-w-full object-contain"
          />
          <div className="mt-3 flex items-center gap-4 text-sm">
            <button
              className="btn btn-secondary"
              onClick={(e) => { e.stopPropagation(); setOpen((i) => Math.max(0, (i ?? 0) - 1)); }}
              disabled={open === 0}
            >
              ← Previous
            </button>
            <span style={{ color: 'hsl(var(--text-muted))' }}>
              {open + 1} / {shots.length} · {shots[open]?.label}
            </span>
            <button
              className="btn btn-secondary"
              onClick={(e) => { e.stopPropagation(); setOpen((i) => Math.min(shots.length - 1, (i ?? 0) + 1)); }}
              disabled={open === shots.length - 1}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
